import {Alert, Button, Space, Table, message} from "antd";
import {FullPageLoader} from "../../../full-page-loader";
import {StyledDescriptionText} from "../../../messages/messages.style";
import type {ColumnsType} from "antd/es/table";
import {StyledPageHeader} from "../../../../styles/dashboard/calls/calls";
import {CloseOutlined, DownloadOutlined} from "@ant-design/icons";
import {
  StyledNoUpcomingCallBox,
  StyledUpcomingCallsWrapper,
} from "../../../calls/upcoming-calls/upcoming-calls.style";
import {
  useDigitalProductCustomerPurchases,
  useDigitalProductExportPurchases,
} from "../../../../features/digital-products";
import {useState} from "react";
import {CustomPagination} from "../../../pagination";
import {StyledSessionDetailsDrawer} from "../../../calls/service/sales-and-insights/customers-section/style";
import {SessionDetailsDrawerBookingDetails} from "../../../calls/service/sales-and-insights/customers-section/booking-details";
import {SessionDetailsDrawerBookingQuestions} from "../../../calls/service/sales-and-insights/customers-section/booking-questions";
import {
  StyledSessionDetailsDrawerBookingDetailsItem,
  StyledSessionDetailsDrawerBookingDetailsWrapper,
} from "../../../calls/service/sales-and-insights/customers-section/booking-details/style";

const PER_PAGE = 5;

interface DataType {
  key: string;
  name: string;
  email: string;
  reference: string;
  id: number;
  date: string;
  price: string;
  bookingQuestions: {answer?: string | null; question: string}[];
  amount: number;
  currency: string;
}

type TProductCustomerInsightsProps = {
  productId: number;
  createdAt?: string;
};

type ProductBookingDetailsDrawerDetailsState = {
  id: number;
  name: string;
  email: string;
  price: string;
  reference: string;
  date: string;
  bookingQuestions: {answer?: string | null; question: string}[];
  amount: number;
  currency: string;
};

export function ProductCustomerInsights({
  productId,
  createdAt,
}: TProductCustomerInsightsProps) {
  const [page, setPage] = useState(1);
  const [isProductBookingDetailsDrawerOpen, setIsProductDetailsDrawerOpen] =
    useState(false);
  const [productDrawerBookingDetails, setProductDrawerBookingDetails] =
    useState<ProductBookingDetailsDrawerDetailsState>();
  const getProductCustomersQuery = useDigitalProductCustomerPurchases(
    productId,
    page,
    PER_PAGE,
    createdAt
  );
  const exportPurchasesMutation = useDigitalProductExportPurchases(productId, createdAt);

  if (getProductCustomersQuery.isLoading) {
    return <FullPageLoader />;
  }
  if (getProductCustomersQuery.isError) {
    return (
      <Alert
        message="Error"
        description={getProductCustomersQuery.error?.message}
        type="error"
        showIcon
      />
    );
  }

  const purchases = getProductCustomersQuery.data;
  const customersData: DataType[] = purchases.data.map((customer) => ({
    key: customer.id.toString(),
    name: `${customer.firstName} ${customer.lastName}`,
    email: customer.emailAddress.toLowerCase(),
    reference: customer.reference,
    date: new Date(customer.createdAt).toDateString(),
    id: customer.id,
    price:
      customer.amount === 0
        ? "Free"
        : `${customer.currency} ${customer.amount.toLocaleString()}`,
    bookingQuestions: customer.bookingQuestions
      ? Object.entries(customer.bookingQuestions).map(([question, answer]) => ({
          question,
          answer,
        }))
      : [],
    amount: customer.amount,
    currency: customer.currency,
  }));

  // Adapt the slice's {total, hasNextPage, hasPrevPage} to CustomPagination's meta.
  const paginationMeta = {
    current_page: page,
    per_page: PER_PAGE,
    total_pages: Math.max(1, Math.ceil((purchases.total ?? 0) / PER_PAGE)),
    next_page: purchases.hasNextPage ? page + 1 : 0,
    previous_page: purchases.hasPrevPage ? page - 1 : 0,
  };

  function showProductBookingDetailsDrawer(
    values: ProductBookingDetailsDrawerDetailsState
  ) {
    setProductDrawerBookingDetails(values);
    setIsProductDetailsDrawerOpen(true);
  }
  function closeProductBookingDetailsDrawer() {
    setIsProductDetailsDrawerOpen(false);
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "17.425rem",
      key: "name",
      render: (text) => (
        <StyledDescriptionText style={{textTransform: "capitalize"}}>
          {text}
        </StyledDescriptionText>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "17.425rem",
      key: "email",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "Reference",
      dataIndex: "reference",
      width: "17.425rem",
      key: "reference",
      render: (text) => <StyledDescriptionText>{text}</StyledDescriptionText>,
    },
    {
      title: "",
      dataIndex: "action",
      width: "17.425rem",
      key: "action",
      render: (text, record) => (
        <Button
          onClick={() =>
            showProductBookingDetailsDrawer({
              date: record.date,
              email: record.email,
              id: record.id,
              name: record.name,
              price: record.price,
              reference: record.reference,
              bookingQuestions: record.bookingQuestions,
              amount: record.amount,
              currency: record.currency,
            })
          }
          block
          type="text"
          size="small"
        >
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  const handleExportAsCSV = () => {
    exportPurchasesMutation.mutate(undefined, {
      onSuccess: (data) => {
        message.success("File ready to be downloaded!");
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "product-customers.csv");
        document.body.appendChild(link);
        link.click();
      },
      onError: (error) =>
        message.error(
          error.response?.data?.message || error.message || "Failed to export CSV"
        ),
    });
  };

  return (
    <>
      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          <StyledPageHeader>Customers</StyledPageHeader>
          <div>
            <Button
              onClick={handleExportAsCSV}
              disabled={customersData.length < 1}
              block
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              loading={exportPurchasesMutation.isLoading}
              size="small"
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </div>
        </div>
        {customersData.length < 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your customers will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={customersData}
              loading={getProductCustomersQuery.isFetching}
              scroll={{x: 100}}
              pagination={false}
            />
            <CustomPagination
              isPreviousData={getProductCustomersQuery.isPreviousData}
              page={page}
              paginationMeta={paginationMeta}
              setPage={setPage}
            />
          </>
        )}
      </div>
      {productDrawerBookingDetails && (
        <StyledSessionDetailsDrawer
          title="Customer details"
          placement="right"
          onClose={closeProductBookingDetailsDrawer}
          visible={isProductBookingDetailsDrawerOpen}
          closable={false}
          extra={
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={closeProductBookingDetailsDrawer}
            ></Button>
          }
        >
          <div>
            <Space direction="vertical" style={{display: "flex"}} size={24}>
              <SessionDetailsDrawerBookingDetails
                data={[
                  {
                    title: "Customer's name",
                    value: productDrawerBookingDetails.name,
                  },
                  {
                    title: "Customer’s email",
                    value: productDrawerBookingDetails.email,
                  },
                  {
                    title: "Date",
                    value: productDrawerBookingDetails.date,
                  },
                  {
                    title: "Reference",
                    value: productDrawerBookingDetails.reference,
                  },
                ]}
              />
              <StyledSessionDetailsDrawerBookingDetailsWrapper>
                <StyledSessionDetailsDrawerBookingDetailsItem>
                  <p className="item-title">Product price</p>
                  <p className="item-value">{`${productDrawerBookingDetails.price}`}</p>
                </StyledSessionDetailsDrawerBookingDetailsItem>
              </StyledSessionDetailsDrawerBookingDetailsWrapper>
              <SessionDetailsDrawerBookingQuestions
                data={productDrawerBookingDetails.bookingQuestions}
              />
            </Space>
          </div>
        </StyledSessionDetailsDrawer>
      )}
    </>
  );
}
